import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { toDatetimeLocal, fromDatetimeLocal } from '../lib/format.js';
import { Page } from '../app/AppShell.js';
import { useToast } from '../components/Toast.js';
import { Loading, ErrorNote } from '../components/ui.js';
import type { Daypart, Layout, ScheduleLayerDraft } from '../lib/types.js';

export function ScheduleScreen(): ReactNode {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const display = useQuery(() => api.display(id), [id]);
  const layers = useQuery(() => api.scheduleLayers(id), [id]);
  const layoutsQ = useQuery(() => api.layouts(), []);
  const layouts = layoutsQ.data ?? [];

  const [drafts, setDrafts] = useState<ScheduleLayerDraft[]>([]);
  const [savingDefault, setSavingDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!layers.data) return;
    setDrafts(
      layers.data.layers
        .filter((l) => l.kind !== 'default')
        .map((l) => ({
          kind: l.kind === 'interrupt' ? 'interrupt' : 'scheduled',
          priority: l.priority,
          layoutId: l.layoutId,
          startsAt: l.startsAt,
          endsAt: l.endsAt,
          dayparts: l.dayparts,
        })),
    );
  }, [layers.data]);

  if (display.error) return <ErrorNote error={display.error} />;
  if (layers.error) return <ErrorNote error={layers.error} />;
  if (!display.data || !layers.data) return <Loading what="schedule" />;

  const d = display.data;

  function setDraft(i: number, next: Partial<ScheduleLayerDraft>): void {
    setDrafts((list) => list.map((x, j) => (j === i ? { ...x, ...next } : x)));
  }

  async function saveDefault(layoutId: string): Promise<void> {
    setSavingDefault(true);
    try {
      await api.updateDisplay(id, { default_layout_id: layoutId || null });
      toast.push('Default layout updated', 'success');
      display.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingDefault(false);
    }
  }

  async function saveLayers(): Promise<void> {
    setSaving(true);
    try {
      const res = await api.replaceScheduleLayers(id, drafts);
      toast.push(`Saved ${res.layers.filter((l) => l.kind !== 'default').length} layer(s)`, 'success');
      layers.refetch();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? new Error(err.fieldMessage() ?? err.message) : err,
      );
    } finally {
      setSaving(false);
    }
  }

  const publishedLayouts = layouts.filter((l) => l.status === 'published');

  return (
    <Page
      title={`Schedule — ${d.name}`}
      actions={
        <button className="btn" onClick={() => navigate('/displays')}>
          Back to displays
        </button>
      }
    >
      <div className="stack" style={{ maxWidth: 820 }}>
        <div className="card card-pad stack">
          <strong>Default layer</strong>
          <p className="muted small" style={{ margin: 0 }}>
            Plays whenever no scheduled or interrupt layer is active. This is the display’s{' '}
            <span className="mono">default_layout_id</span>.
          </p>
          <label className="field" style={{ maxWidth: 360 }}>
            Layout
            <select
              value={d.defaultLayoutId ?? ''}
              disabled={savingDefault}
              onChange={(e) => void saveDefault(e.target.value)}
            >
              <option value="">— none —</option>
              {layouts.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.status !== 'published' ? ' (draft)' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="card card-pad stack">
          <div className="row between">
            <strong>Scheduled &amp; interrupt layers</strong>
            <button
              className="btn small"
              onClick={() =>
                setDrafts((list) => [
                  ...list,
                  {
                    kind: 'scheduled',
                    priority: (list.at(-1)?.priority ?? 0) + 10,
                    layoutId: publishedLayouts[0]?.id ?? layouts[0]?.id ?? '',
                    startsAt: null,
                    endsAt: null,
                    dayparts: [],
                  },
                ])
              }
            >
              Add layer
            </button>
          </div>

          {drafts.length === 0 ? (
            <p className="faint small" style={{ margin: 0 }}>
              No windowed layers. The default layer plays around the clock.
            </p>
          ) : (
            drafts.map((layer, i) => (
              <LayerRow
                key={i}
                layer={layer}
                layouts={layouts}
                onChange={(next) => setDraft(i, next)}
                onRemove={() => setDrafts((list) => list.filter((_, j) => j !== i))}
              />
            ))
          )}

          <div className="row">
            <button className="btn primary" disabled={saving} onClick={() => void saveLayers()}>
              {saving ? 'Saving…' : 'Save layers'}
            </button>
            <span className="faint small">
              Higher priority wins. Players evaluate windows and dayparts against corrected time.
            </span>
          </div>
        </div>
      </div>
    </Page>
  );
}

function LayerRow({
  layer,
  layouts,
  onChange,
  onRemove,
}: {
  layer: ScheduleLayerDraft;
  layouts: Layout[];
  onChange: (next: Partial<ScheduleLayerDraft>) => void;
  onRemove: () => void;
}): ReactNode {
  function setDaypart(i: number, next: Partial<Daypart>): void {
    onChange({ dayparts: layer.dayparts.map((dp, j) => (j === i ? { ...dp, ...next } : dp)) });
  }

  return (
    <div className="region-block stack">
      <div className="row wrap">
        <label className="field small" style={{ width: 120 }}>
          Kind
          <select
            value={layer.kind}
            onChange={(e) => onChange({ kind: e.target.value === 'interrupt' ? 'interrupt' : 'scheduled' })}
          >
            <option value="scheduled">scheduled</option>
            <option value="interrupt">interrupt</option>
          </select>
        </label>
        <label className="field small" style={{ width: 90 }}>
          Priority
          <input
            type="number"
            value={layer.priority}
            onChange={(e) => onChange({ priority: Number(e.target.value) || 0 })}
          />
        </label>
        <label className="field small" style={{ flex: 1, minWidth: 180 }}>
          Layout
          <select value={layer.layoutId} onChange={(e) => onChange({ layoutId: e.target.value })}>
            <option value="">— pick —</option>
            {layouts.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.status !== 'published' ? ' (draft)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button className="btn ghost small danger" onClick={onRemove}>
          Remove
        </button>
      </div>

      <div className="row wrap">
        <label className="field small">
          Starts
          <input
            type="datetime-local"
            value={toDatetimeLocal(layer.startsAt)}
            onChange={(e) => onChange({ startsAt: fromDatetimeLocal(e.target.value) })}
          />
        </label>
        <label className="field small">
          Ends
          <input
            type="datetime-local"
            value={toDatetimeLocal(layer.endsAt)}
            onChange={(e) => onChange({ endsAt: fromDatetimeLocal(e.target.value) })}
          />
        </label>
      </div>

      <div className="stack" style={{ gap: 6 }}>
        <span className="faint small">Dayparts (optional)</span>
        {layer.dayparts.map((dp, di) => (
          <div key={di} className="row wrap small">
            <input
              placeholder="days e.g. mon-fri"
              value={dp.days}
              onChange={(e) => setDaypart(di, { days: e.target.value })}
              style={{ width: 130 }}
            />
            <input
              placeholder="on 08:00"
              value={dp.on}
              onChange={(e) => setDaypart(di, { on: e.target.value })}
              style={{ width: 90 }}
            />
            <input
              placeholder="off 18:00"
              value={dp.off}
              onChange={(e) => setDaypart(di, { off: e.target.value })}
              style={{ width: 90 }}
            />
            <button
              className="btn ghost small danger"
              onClick={() => onChange({ dayparts: layer.dayparts.filter((_, j) => j !== di) })}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          className="btn ghost small"
          onClick={() => onChange({ dayparts: [...layer.dayparts, { days: '', on: '', off: '' }] })}
        >
          + daypart
        </button>
      </div>
    </div>
  );
}
